import React, {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useRef
} from 'react'

import classnames from 'classnames'
import FocusTrap from 'focus-trap-react'
import { AnimatePresence, motion } from 'framer-motion'

import { t } from 'src/translations'

import ErrorBoundary from 'shared/components/common/error_boundary/ErrorBoundary'
import useIsClient from 'shared/js/hooks/useIsClient'

import Portal from './Portal'

type ModalContextType = {
  onClose: () => void
  preventOverlayClose?: boolean
}

type OverlayProps = {
  fadeIn?: boolean
  fadeOut?: boolean
  opacity?: number
  color?: string
  style?: React.CSSProperties | undefined
  duration?: number
  testId?: string
}

type Props = {
  // Prevents clicking outside the modal from closing it
  preventOverlayClose?: boolean
  isOpen: boolean
  onClose: () => void
  className?: string
  testId?: string
  animateClose?: boolean
  freezeBackground?: boolean
}

type ContentProps = {
  wrapperClassName?: string
  contentClassName?: string
  contentRef?: React.Ref<HTMLDivElement>
  slideIn?: boolean
  slideOut?: boolean
  fadeIn?: boolean
  style?: React.CSSProperties | undefined
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  disableFocusTrap?: boolean
  initialFocusElementId?: string
  returnFocusElementId?: string
}

export const ModalContext = createContext<ModalContextType>({
  onClose: () => {}
})

export const ModalOverlay = ({
  fadeIn,
  fadeOut,
  opacity,
  color,
  duration,
  style,
  testId
}: OverlayProps) => {
  const { onClose, preventOverlayClose } = useModalContext()
  const isClient = useIsClient()

  const onOverlayClick = useCallback(() => {
    if (!preventOverlayClose) {
      onClose()
    }
  }, [preventOverlayClose, onClose])

  const targetOpacity = typeof opacity === 'number' ? opacity / 100 : undefined

  return (
    <motion.div
      initial={fadeIn && isClient ? { opacity: 0 } : undefined}
      animate={fadeIn ? { opacity: targetOpacity || 1 } : undefined}
      exit={fadeIn || fadeOut ? { opacity: 0 } : undefined}
      transition={{ duration: duration ?? 0.2 }}
      onClick={onOverlayClick}
      style={{ opacity: targetOpacity, backgroundColor: color, ...style }}
      className='modalOverlay'
      data-testid={testId}
    />
  )
}

// A version of ModalContent that will add a scrollable class if the content is taller than the window
export const ScrollableModalContent = (
  props: React.PropsWithChildren<ContentProps>
) => {
  useEffect(() => {
    const el = document.querySelector(`.${props.contentClassName}`)
    if (!el) {
      return
    }
    const modalWrapper = document.querySelector('.modalWrapper')
    const padding = modalWrapper
      ? parseInt(window.getComputedStyle(modalWrapper).paddingTop)
      : 0
    // top-padding + bottom-padding + content height
    const height = padding * 2 + el.scrollHeight
    if (window.innerHeight < height) {
      if (!el.classList.contains('scrollable')) {
        el.classList.add('scrollable')
      }
    } else {
      if (el.classList.contains('scrollable')) {
        el.classList.remove('scrollable')
      }
    }
  })
  return <ModalContent {...props} />
}

export const ModalContent = (props: React.PropsWithChildren<ContentProps>) => {
  const isClient = useIsClient()
  // We need to create unique IDs for the modal content so the focus trap does not get stuck in a loop
  const modalContentId = `modal-content-${useId().replace(/:/g, '')}`
  const focusElementId =
    props.initialFocusElementId ?? props.ariaLabelledBy ?? modalContentId

  // Fixes sentry issue 7382659905
  // Live ref to the modal content div. Using a ref (instead of document.getElementById)
  // ensures fallbackFocus always reads current DOM state — React zeroes it on unmount,
  // so stale FocusTrap timers gracefully fall back to document.body instead of throwing.
  const internalRef = useRef<HTMLDivElement | null>(null)

  // Stable merged ref: keeps internalRef in sync and forwards to props.contentRef.
  const mergedRef = useCallback(
    (el: HTMLDivElement | null) => {
      internalRef.current = el
      if (!props.contentRef) return
      if (typeof props.contentRef === 'function') {
        props.contentRef(el)
      } else {
        ;(
          props.contentRef as React.MutableRefObject<HTMLDivElement | null>
        ).current = el
      }
    },
    [props]
  )

  const contentDiv = (
    <div
      className={classnames('modalWrapper', props.wrapperClassName)}
      role='dialog'
      aria-modal='true'
      aria-labelledby={props.ariaLabelledBy}
      aria-describedby={props.ariaDescribedBy}
    >
      <motion.div
        initial={
          props.slideIn && isClient
            ? { translateX: '100vw' }
            : props.fadeIn && isClient
            ? { opacity: 0 }
            : undefined
        }
        animate={
          props.slideIn
            ? { translateX: '0vw' }
            : props.fadeIn
            ? { opacity: 1 }
            : undefined
        }
        exit={
          props.slideIn || props.slideOut
            ? { translateX: '100vw' }
            : props.fadeIn
            ? { opacity: 0 }
            : undefined
        }
        transition={{ duration: props.slideIn ? 0.5 : 0.2 }}
        className={classnames('modalContent', props.contentClassName)}
        style={props.style}
        ref={mergedRef}
        data-testid='modal-content'
        id={modalContentId}
        tabIndex={-1}
      >
        {props.children}
      </motion.div>
    </div>
  )

  return (
    <ErrorBoundary>
      {props.disableFocusTrap ? (
        contentDiv
      ) : (
        <FocusTrap
          focusTrapOptions={{
            allowOutsideClick: true,
            initialFocus: () =>
              document.getElementById(focusElementId) ?? undefined,
            ...(focusElementId
              ? {
                  checkCanFocusTrap: () =>
                    new Promise<void>((resolve) => {
                      setTimeout(() => {
                        // Only activate if the modal is still mounted. If the modal closed
                        // during the delay, internalRef.current is null and we skip
                        // resolution — focus-trap never activates and fallbackFocus is
                        // never called.
                        if (internalRef.current) resolve()
                      }, 500)
                    })
                }
              : {}),
            fallbackFocus: () => internalRef.current ?? document.body,
            setReturnFocus: props.returnFocusElementId
              ? () =>
                  (document.querySelector(
                    `#${props.returnFocusElementId}`
                  ) as HTMLElement) || false
              : undefined
          }}
        >
          {contentDiv}
        </FocusTrap>
      )}
    </ErrorBoundary>
  )
}

type ModalCloseButtonProps = {
  className?: string
  onClose?: () => void
  tabIndex?: number
}

export const ModalCloseButton = ({
  className,
  onClose,
  tabIndex
}: ModalCloseButtonProps) => {
  const { onClose: defaultClose } = useModalContext()

  return (
    <button
      data-testid='modal-close-button'
      aria-label={t('modal.close')}
      type='button'
      className={classnames('closeButton', className)}
      onClick={onClose ?? defaultClose}
      tabIndex={tabIndex}
    >
      <svg
        xmlns='http://www.w3.org/2000/svg'
        style={{ display: 'block' }}
        width='15'
        height='15'
        viewBox='0 0 15 15'
        fill='none'
      >
        <path
          d='M1 0.875L14 13.875M14 0.875L1 13.875'
          stroke='currentColor'
          strokeWidth='1.5'
          strokeMiterlimit='10'
          strokeLinecap='round'
          strokeLinejoin='round'
        />
      </svg>
    </button>
  )
}

export const Modal = (props: React.PropsWithChildren<Props>) => {
  const {
    onClose,
    preventOverlayClose,
    animateClose = true,
    isOpen,
    freezeBackground = true
  } = props

  useEffect(() => {
    // Disallow scrolling of the background when modal is open if freezeBackground is true
    if (isOpen)
      document.body.style.overflowY = freezeBackground ? 'hidden' : 'unset'
    return () => {
      if (isOpen) document.body.style.overflowY = 'unset'
    }
  }, [freezeBackground, isOpen])

  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !preventOverlayClose) {
        onClose()
      }
    },
    [onClose, preventOverlayClose]
  )

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
    }
    return () => {
      if (isOpen) {
        document.removeEventListener('keydown', handleEscape)
      }
    }
  }, [handleEscape, isOpen])

  const Wrapper = animateClose ? AnimatePresence : Fragment
  return (
    <ModalContext.Provider
      value={{
        onClose: props.onClose,
        preventOverlayClose: props.preventOverlayClose
      }}
    >
      <Wrapper>
        {props.isOpen && (
          <Portal>
            <div
              className={classnames('modal', props.className)}
              data-testid={props.testId}
            >
              {props.children}
            </div>
          </Portal>
        )}
      </Wrapper>
    </ModalContext.Provider>
  )
}

export const useModalContext = () => {
  return useContext(ModalContext)
}
